/**
 * Google Geocoder Service
 * Provides geocoding functionality using Google Maps Geocoding API
 * with a Nominatim-compatible interface
 */
class GoogleGeocoderService {
  constructor(googleMapsApi) {
    if (!googleMapsApi || !googleMapsApi.Geocoder) {
      throw new Error("Google Maps API with Geocoder is required");
    }

    this.googleMapsApi = googleMapsApi;
    this.geocoder = new googleMapsApi.Geocoder();
  }

  /**
   * Search for locations using Google Geocoding API
   * Returns results in OSM Nominatim format for compatibility
   * @param {string} query - Search query
   * @param {Object} params - Query parameters (format, limit, etc.)
   * @returns {Promise<Array>} Results in Nominatim format
   */
  async search(query, params = {}) {
    if (!query || !query.trim()) {
      return [];
    }

    return new Promise((resolve, reject) => {
      this.geocoder.geocode(
        {
          address: query
        },
        (results, status) => {
          if (status === this.googleMapsApi.GeocoderStatus.OK) {
            // Convert Google results to Nominatim format
            const nominatimResults = results
              .slice(0, params.limit || 5)
              .map(result => ({
                display_name: result.formatted_address,
                lat: result.geometry.location.lat().toString(),
                lon: result.geometry.location.lng().toString(),
                place_id: result.place_id,
                osm_type: "way", // Nominatim compatibility
                osm_id: result.place_id, // Use Google place_id as osm_id
                type: result.types[0] || "unknown",
                importance: 1, // Google doesn't provide importance scores
                boundingbox: result.geometry.bounds
                  ? [
                      result.geometry.bounds.getSouthWest().lat().toString(),
                      result.geometry.bounds.getNorthEast().lat().toString(),
                      result.geometry.bounds.getSouthWest().lng().toString(),
                      result.geometry.bounds.getNorthEast().lng().toString()
                    ]
                  : null
              }));

            resolve(nominatimResults);
          } else if (
            status === this.googleMapsApi.GeocoderStatus.ZERO_RESULTS
          ) {
            resolve([]);
          } else {
            reject(new Error(`Google Geocoding failed with status: ${status}`));
          }
        }
      );
    });
  }

  /**
   * Reverse geocode coordinates to get address
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @returns {Promise<Array>} Results in Nominatim format
   */
  async reverse(lat, lng) {
    return new Promise((resolve, reject) => {
      const latlng = new this.googleMapsApi.LatLng(lat, lng);

      this.geocoder.geocode(
        {
          location: latlng
        },
        (results, status) => {
          if (status === this.googleMapsApi.GeocoderStatus.OK) {
            // Convert to Nominatim format
            const nominatimResults = results.map(result => ({
              display_name: result.formatted_address,
              lat: lat.toString(),
              lon: lng.toString(),
              place_id: result.place_id,
              osm_type: "way",
              osm_id: result.place_id,
              type: result.types[0] || "unknown",
              importance: 1
            }));

            resolve(nominatimResults);
          } else if (
            status === this.googleMapsApi.GeocoderStatus.ZERO_RESULTS
          ) {
            resolve([]);
          } else {
            reject(
              new Error(`Reverse geocoding failed with status: ${status}`)
            );
          }
        }
      );
    });
  }
}

export default GoogleGeocoderService;
